/**
 * Topbar - sticky top bar with breadcrumbs and user actions.
 * Backdrop-blur, auto-derived breadcrumbs from route + navConfig.
 */

import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  MenuOutlined,
  LightModeOutlined,
  DarkModeOutlined,
  SettingsBrightnessOutlined,
  ChevronRight,
} from '@mui/icons-material';
import { useThemeMode, type ThemeMode } from '../../theme';
import { navConfig } from '../Sidebar/navConfig';
import { MOBILE_BP } from '../Sidebar/Sidebar';

interface Props {
  onMobileMenuOpen: () => void;
}

const Bar = styled('header')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 56,
  padding: '0 20px',
  backgroundColor: theme.tokens.topbarBg,
  backdropFilter: 'blur(12px)',
  borderBottom: `1px solid ${theme.tokens.topbarBorder}`,
}));

const Breadcrumbs = styled('nav')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minWidth: 0,
});

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.tokens.fgSecondary,
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  '&:hover': { color: theme.tokens.fgPrimary },
}));

const BreadcrumbCurrent = styled('span')(({ theme }) => ({
  color: theme.tokens.fgPrimary,
  fontSize: 14,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const Actions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

const themeIcons: Record<ThemeMode, React.ReactElement> = {
  light: <LightModeOutlined fontSize="small" />,
  dark: <DarkModeOutlined fontSize="small" />,
  system: <SettingsBrightnessOutlined fontSize="small" />,
};

const nextMode: Record<ThemeMode, ThemeMode> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

function useBreadcrumbs() {
  const { pathname } = useLocation();

  if (pathname === '/' || pathname === '/dashboard') {
    return [{ label: 'Dashboard', to: '/' }];
  }

  // Find the most specific match (longest `to` path) - avoids '/' catching everything
  let bestItem: (typeof navConfig)[number]['items'][number] | null = null;

  for (const cat of navConfig) {
    for (const item of cat.items) {
      if (item.to === '/') continue; // skip root, it matches everything
      if (pathname === item.to || pathname.startsWith(item.to + '/')) {
        if (!bestItem || item.to.length > bestItem.to.length) {
          bestItem = item;
        }
      }
    }
  }

  if (bestItem) {
    return [
      { label: 'Dashboard', to: '/' },
      { label: bestItem.label, to: bestItem.to },
    ];
  }

  return [{ label: 'Dashboard', to: '/' }];
}

export function Topbar({ onMobileMenuOpen }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BP));
  const { mode, setMode } = useThemeMode();
  const crumbs = useBreadcrumbs();

  return (
    <Bar>
      <Breadcrumbs aria-label="breadcrumbs">
        {isMobile && (
          <IconButton
            onClick={onMobileMenuOpen}
            size="small"
            aria-label="Open menu"
            sx={{ mr: 1, color: theme.tokens.fgSecondary }}
          >
            <MenuOutlined />
          </IconButton>
        )}
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <React.Fragment key={crumb.to}>
              {i > 0 && <ChevronRight sx={{ fontSize: 16, color: theme.tokens.fgTertiary }} />}
              {isLast ? (
                <BreadcrumbCurrent>{crumb.label}</BreadcrumbCurrent>
              ) : (
                <BreadcrumbLink to={crumb.to}>{crumb.label}</BreadcrumbLink>
              )}
            </React.Fragment>
          );
        })}
      </Breadcrumbs>

      <Actions>
        <Tooltip title={`Theme: ${mode}`}>
          <IconButton
            onClick={() => setMode(nextMode[mode])}
            size="small"
            aria-label="Toggle theme"
            sx={{ color: theme.tokens.fgSecondary }}
          >
            {themeIcons[mode]}
          </IconButton>
        </Tooltip>
      </Actions>
    </Bar>
  );
}
